// Copyright 2022 Linka Cloud  All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

interface TypingEffectProps {
  speed: number;
  eraseSpeed: number;
  typingDelay: number;
  eraseDelay: number;
  text: string[] | string;
  cursor?: string;
}

const TypingEffect = ({
  speed = 200,
  eraseSpeed = 200,
  typingDelay = 5000,
  eraseDelay = 2500,
  text,
  cursor,
  ...rest
}: TypingEffectProps) => {
  const [action, setAction] = useState<'type' | 'erase' | null>(null);
  const [index, setIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const timeoutRef = useRef<any>();

  const getRawText = () => (typeof text === 'string' ? [text] : [...text]);

  const type = () => {
    if (action !== 'type') {
      setAction('type');
    }
    const text = getRawText()[index];
    if (text.length > displayText.length) {
      setDisplayText(text.substr(0, displayText.length + 1));
    }
  };

  const erase = () => {
    if (action !== 'erase') {
      setAction('erase');
    }
    if (displayText.length === 0) {
      const textArray = getRawText();
      setIndex(index + 1 === textArray.length ? 0 : index + 1);
      setAction(null);
    } else {
      setDisplayText(displayText.substring(-displayText.length, displayText.length - 1));
    }
  };

  useEffect(() => {
    const text = getRawText()[index];
    if (action !== 'erase' && text.length > displayText.length) {
      timeoutRef.current = setTimeout(type, action !== 'type' ? typingDelay : speed);
    } else {
      timeoutRef.current = setTimeout(erase, action !== 'erase' ? eraseDelay : eraseSpeed);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [typingDelay, displayText, index, action]);

  return (
    <span {...rest}>
      <div style={{ display: 'inline-block' }}>{displayText}</div>
      <Cursor cursor={cursor} />
    </span>
  );
};
TypingEffect.displayName = 'TypingEffect';

interface CursorProps {
  cursor?: string;
}

const Cursor = ({ cursor = '|' }: CursorProps) => {
  const [display, setDisplay] = useState(true);
  const intervalRef = useRef<any>();
  const style = {
    display: 'inline-block',
    transition: 'opacity 0.5s',
    opacity: display ? 1 : 0,
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => setDisplay(!display), 500);
    return () => clearInterval(intervalRef.current);
  }, [display]);
  return <span style={style}>{cursor}</span>;
};

export const TypingMessage = styled(TypingEffect)(({ theme }) => ({
  width: '100%',

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  fontFamily: 'Courier New,Courier,Lucida Sans Typewriter,Lucida Typewriter,monospace',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.secondary,
  fontSize: '3rem',
  marginBottom: theme.spacing(8),
  '& > span': {
    backgroundColor: theme.palette.background.default,
  },
}));
TypingMessage.displayName = 'TypingMessage';
